import { TweenListObj } from './TweenListObj';
import { PropertyInfoObj } from './PropertyInfoObject';
import { AuxFunctions } from './AuxFunctions';
import { SpecialPropertySplitter } from './SpecialPropertySplitter';
import { Equations } from './Equations';
import { SpecialProperty } from './SpecialProperty';
import { SpecialPropertyModifier } from './SpecialPropertyModifier';


interface TransitionParameters {
    /**
     * Time in seconds or frames for the tweening to take (defaults 2)
     */
    time: number,
    /**
     * Delay time (defaults 0)
     */
    delay: number,
    /**
     * Type of transition equation... (defaults to "easeOutExpo")
     */
    transition: Transition | TransitionFunction,
    /**
     * Direct property, See the TweenListObj class
     */
    onStart: Function,
    /**
     * Direct property, See the TweenListObj class
     */
    onUpdate: Function,
    /**
     * Direct property, See the TweenListObj class
     */
    onComplete: Function,
    /**
     * Direct property, See the TweenListObj class
     */
    onStartParams: any[],
    /**
     * Direct property, See the TweenListObj class
     */
    onUpdateParams: any[],
    /**
     * Direct property, See the TweenListObj class
     */
    onCompleteParams: any[]
}

export interface CallerParameters extends TransitionParameters {
    /**
     * Number of times this caller should be called
     */
    count: number,
    /**
     * Whether to wait (or not) one frame for each call
     */
    waitFrames: boolean
}

/**
 * Interface for tween parameters
 */
export interface TweenParameters extends TransitionParameters {

    /**
     * Whether to use frames instead of seconds for time control (defaults false)
     */
    useFrames: boolean,
    /**
     * Direct property, See the TweenListObj class
     */
    onOverwrite: Function,
    /**
     * Direct property, See the TweenListObj class
     */
    onOverwriteParams: any[],
    /**
     * Direct property, See the TweenListObj class
     */
    rounded: boolean,
    /**
     * Direct property, See the TweenListObj class
     */
    skipUpdates: boolean,

    /**
     * One or more of the tweened object tweenable properties
     */
    [tweenableProperties: string]: unknown;
}

export type Transition =
    'easeNone'
    | 'linear'

    | 'easeInQuad'
    | 'easeOutQuad'
    | 'easeInOutQuad'
    | 'easeOutInQuad'

    | 'easeInCubic'
    | 'easeOutCubic'
    | 'easeInOutCubic'
    | 'easeOutInCubic'

    | 'easeInQuart'
    | 'easeOutQuart'
    | 'easeInOutQuart'
    | 'easeOutInQuart'

    | 'easeInQuint'
    | 'easeOutQuint'
    | 'easeInOutQuint'
    | 'easeOutInQuint'

    | 'easeInSine'
    | 'easeOutSine'
    | 'easeInOutSine'
    | 'easeOutInSine'

    | 'easeInCirc'
    | 'easeOutCirc'
    | 'easeInOutCirc'
    | 'easeOutInCirc'

    | 'easeInExpo'
    | 'easeOutExpo'
    | 'easeInOutExpo'
    | 'easeOutInExpo'

    | 'easeInElastic'
    | 'easeOutElastic'
    | 'easeInOutElastic'
    | 'easeOutInElastic'

    | 'easeInBack'
    | 'easeOutBack'
    | 'easeInOutBack'
    | 'easeOutInBack'

    | 'easeInBounce'
    | 'easeOutBounce'
    | 'easeInOutBounce'
    | 'easeOutInBounce'

    | string;

export type TransitionFunction = (t: number, b: number, c: number, d: number, p_params: any) => number;

export class Tweener {

    /**
     * Whether or not the engine is currently running
     * @private
     */
    private static _engineExists: boolean = false;
    /**
     * Whether or not the class has been initiated
     * @private
     */
    private static _inited: boolean = false;
    /**
     * The current time. This is generic for all tweenings for a "time grid" based update
     * @private
     */
    private static _currentTime: number;
    /**
     * The current frame. Used on frame-based tweenings
     * @private
     */
    private static _currentTimeFrame: number;

    /**
     * List of active tweens
     * @private
     */
    private static _tweenList: TweenListObj[];

    /**
     * Time scale (default = 1)
     * @private
     */
    private static _timeScale: number = 1;

    /**
     * List of "pre-fetched" transition functions
     * @private
     */
    private static _transitionList: Map<Transition, TransitionFunction>;

    private static _specialPropertyList: any;			// List of special properties
    private static _specialPropertyModifierList: any;	// List of special property modifiers
    private static _specialPropertySplitterList: any;	// List of special property splitters

    public static autoOverwrite: boolean = true;		// If true, auto overwrite on new tweens is on unless declared as false

    /***
     * There's no constructor.
     * @private
     */
    constructor() {
        throw new Error('Tweener is a static class and should not be instantiated.');
    }

    // ==================================================================================================================================
    // TWEENING CONTROL functions -------------------------------------------------------------------------------------------------------

    /***
     * Adds a new tweening.
     *
     * @param p_scopes Object that should be tweened: a DisplayObject, Text, etc. OR an array of objects
     * @param p_parameters Object containing the specified parameters in any order, as well as the properties that should be tweened and their values
     *
     * @return <code>true</code> if the tween was successfully added, <code>false</code> if otherwise
     */
    public static addTween(p_scopes: unknown, p_parameters: Partial<TweenParameters> = null): boolean {
        if (p_scopes === null) return false;

        let i: number,
            j: number,
            istr: string;

        let rScopes: any[]; // List of objects to tween
        if (p_scopes instanceof Array) {
            // The first argument is an array
            rScopes = p_scopes.concat();
        } else {
            // The first argument(s) is(are) object(s)
            rScopes = [p_scopes];
        }

        // make properties chain ("inheritance")
        const p_obj: any = TweenListObj.makePropertiesChain(p_parameters);

        // Creates the main engine if it isn't active
        if (!Tweener._inited) Tweener.init();
        if (!Tweener._engineExists) Tweener.startEngine(); // Quick fix for not resetting the vars on double ctrl+enter...

        // Creates a "safer", more strict tweening object
        const rTime: number = (isNaN(p_obj.time) ? 0 : p_obj.time); // Real time
        const rDelay: number = (isNaN(p_obj.delay) ? 0 : p_obj.delay); // Real delay

        // Creates the property list; everything that isn't a hardcoded variable
        const rProperties: PropertyInfoObj[] = []; // Object containing a list of PropertyInfoObj instances
        const restrictedWords: any = {
            overwrite: true,
            time: true,
            delay: true,
            useFrames: true,
            skipUpdates: true,
            transition: true,
            transitionParams: true,
            onStart: true,
            onUpdate: true,
            onComplete: true,
            onOverwrite: true,
            onError: true,
            rounded: true,
            onStartParams: true,
            onUpdateParams: true,
            onCompleteParams: true,
            onOverwriteParams: true,
            onStartScope: true,
            onUpdateScope: true,
            onCompleteScope: true,
            onOverwriteScope: true,
            onErrorScope: true
        };
        const modifiedProperties: any = {};
        for (istr in p_obj) {
            if (!restrictedWords[istr]) {
                // It's an additional pair, so adds
                if (Tweener._specialPropertySplitterList[istr]) {
                    // Special property splitter
                    const splitProperties: Array<any> = Tweener._specialPropertySplitterList[istr].splitValues(p_obj[istr], Tweener._specialPropertySplitterList[istr].parameters);
                    for (i = 0; i < splitProperties.length; i++) {
                        if (Tweener._specialPropertySplitterList[splitProperties[i].name]) {
                            const splitProperties2: Array<any> = Tweener._specialPropertySplitterList[splitProperties[i].name].splitValues(splitProperties[i].value, Tweener._specialPropertySplitterList[splitProperties[i].name].parameters);
                            for (j = 0; j < splitProperties2.length; j++) {
                                rProperties[splitProperties2[j].name] = new PropertyInfoObj({
                                    valueStart: undefined,
                                    valueComplete: splitProperties2[j].value,
                                    arrayIndex: splitProperties2[j].arrayIndex,
                                    isSpecialProperty: false
                                });
                            }
                        } else {
                            rProperties[splitProperties[i].name] = new PropertyInfoObj({
                                valueStart: undefined,
                                valueComplete: splitProperties[i].value,
                                arrayIndex: splitProperties[i].arrayIndex,
                                isSpecialProperty: false
                            });
                        }
                    }
                } else if (Tweener._specialPropertyModifierList[istr] != undefined) {
                    // Special property modifier
                    const tempModifiedProperties: Array<any> = Tweener._specialPropertyModifierList[istr].modifyValues(p_obj[istr]);
                    for (i = 0; i < tempModifiedProperties.length; i++) {
                        modifiedProperties[tempModifiedProperties[i].name] = {
                            modifierParameters: tempModifiedProperties[i].parameters,
                            modifierFunction: Tweener._specialPropertyModifierList[istr].getValue
                        };
                    }
                } else {
                    // Regular property or special property, just add the property normally
                    rProperties[istr] = {
                        valueStart: undefined,
                        valueComplete: p_obj[istr]
                    };
                }
            }
        }

        // Verifies whether the properties exist or not, for warning messages
        for (istr in rProperties) {
            if (Tweener._specialPropertyList[istr] != undefined) {
                rProperties[istr].isSpecialProperty = true;
            } else {
                if (rScopes[0][istr] == undefined) {
                    Tweener.printError('The property \'' + istr + '\' doesn\'t seem to be a normal object property of ' + rScopes[0] + ' or a registered special property.');
                }
            }
        }

        // Adds the modifiers to the list of properties
        for (istr in modifiedProperties) {
            if (rProperties[istr] != undefined) {
                rProperties[istr].modifierParameters = modifiedProperties[istr].modifierParameters;
                rProperties[istr].modifierFunction = modifiedProperties[istr].modifierFunction;
            }

        }

        let rTransition: TransitionFunction; // Real transition

        if (typeof p_obj.transition == 'string') {
            // string parameter, transition names
            const trans: string = p_obj.transition.toLowerCase();
            rTransition = Tweener._transitionList.get(trans);
        } else {
            // Proper transition function
            rTransition = p_obj.transition;
        }
        if (!(rTransition)) rTransition = Tweener._transitionList.get('easeOutExpo'.toLowerCase());

        let nProperties: any;
        let nTween: TweenListObj;
        let myT: number;

        for (i = 0; i < rScopes.length; i++) {
            // Makes a copy of the properties
            nProperties = {};
            for (istr in rProperties) {
                nProperties[istr] = new PropertyInfoObj(rProperties[istr]);
            }

            if (p_obj.useFrames == true) {
                nTween = new TweenListObj(
                    /* scope			*/    rScopes[i],
                    /* timeStart		*/    Tweener._currentTimeFrame + (rDelay / Tweener._timeScale),
                    /* timeComplete		*/    Tweener._currentTimeFrame + ((rDelay + rTime) / Tweener._timeScale),
                    /* useFrames		*/    true,
                    /* transition		*/    rTransition,
                    p_obj.transitionParams
                );
            } else {
                nTween = new TweenListObj(
                    /* scope			*/    rScopes[i],
                    /* timeStart		*/    Tweener._currentTime + ((rDelay * 1000) / Tweener._timeScale),
                    /* timeComplete		*/    Tweener._currentTime + (((rDelay * 1000) + (rTime * 1000)) / Tweener._timeScale),
                    /* useFrames		*/    false,
                    /* transition		*/    rTransition,
                    p_obj.transitionParams
                );
            }

            nTween.properties = nProperties;
            nTween.onStart = p_obj.onStart;
            nTween.onUpdate = p_obj.onUpdate;
            nTween.onComplete = p_obj.onComplete;
            nTween.onOverwrite = p_obj.onOverwrite;
            nTween.onError = p_obj.onError;
            nTween.onStartParams = p_obj.onStartParams;
            nTween.onUpdateParams = p_obj.onUpdateParams;
            nTween.onCompleteParams = p_obj.onCompleteParams;
            nTween.onOverwriteParams = p_obj.onOverwriteParams;
            nTween.onStartScope = p_obj.onStartScope;
            nTween.onUpdateScope = p_obj.onUpdateScope;
            nTween.onCompleteScope = p_obj.onCompleteScope;
            nTween.onOverwriteScope = p_obj.onOverwriteScope;
            nTween.onErrorScope = p_obj.onErrorScope;
            nTween.rounded = p_obj.rounded;
            nTween.skipUpdates = p_obj.skipUpdates;

            // Remove other tweenings that occur at the same time
            if (p_obj.overwrite == undefined ? Tweener.autoOverwrite : p_obj.overwrite) Tweener.removeTweensByTime(nTween.scope, nTween.properties, nTween.timeStart, nTween.timeComplete); // Changed on 1.32.74

            // And finally adds it to the list
            Tweener._tweenList.push(nTween);

            // Immediate update and removal if it's an immediate tween -- if not deleted, it executes at the end of this frame execution
            if (rTime == 0 && rDelay == 0) {
                myT = Tweener._tweenList.length - 1;
                Tweener.updateTweenByIndex(myT);
                Tweener.removeTweenByIndex(myT);
            }
        }

        return true;
    }

    // A "caller" is like this: [          |     |  | ||] got it? :)
    // this function is crap - should be fixed later/extend on addTween()

    /**
     * Adds a new caller tweening.
     *
     * @param p_scopes Object that should be tweened: a movieclip, textfield, etc. OR an array of objects
     * @param p_parameters Object containing the specified parameters in any order, as well as the properties that should be tweened and their values
     *
     * @return <code>true</code> if the tween was successfully added, <code>false</code> if otherwise.
     */
    public static addCaller(p_scopes: unknown = null, p_parameters: Partial<CallerParameters> = null): boolean {
        if (!(p_scopes)) return false;

        let i: number;

        let rScopes: Array<any>; // List of objects to tween
        if (p_scopes instanceof Array) {
            // The first argument is an array
            rScopes = p_scopes.concat();
        } else {
            // The first argument(s) is(are) object(s)
            rScopes = [p_scopes];
        }

        const p_obj: any = p_parameters;

        // Creates the main engine if it isn't active
        if (!Tweener._inited) Tweener.init();
        if (!Tweener._engineExists) Tweener.startEngine();

        // Creates a "safer", more strict tweening object
        const rTime: number = (isNaN(p_obj.time) ? 0 : p_obj.time); // Real time
        const rDelay: number = (isNaN(p_obj.delay) ? 0 : p_obj.delay); // Real delay

        let rTransition: Function = undefined; // Real transition
        if (typeof p_obj.transition == 'string') {
            // string parameter, transition names
            const trans: string = p_obj.transition.toLowerCase();
            rTransition = Tweener._transitionList.get(trans);
        } else {
            // Proper transition function
            rTransition = p_obj.transition;
        }

        if(rTransition == undefined) {
            rTransition = Tweener._transitionList.get('easeOutExpo'.toLowerCase());
        }

        let nTween: TweenListObj;
        let myT: number;
        for (i = 0; i < rScopes.length; i++) {

            if (p_obj.useFrames == true) {
                nTween = new TweenListObj(
                    /* scope			*/    rScopes[i],
                    /* timeStart		*/    Tweener._currentTimeFrame + (rDelay / Tweener._timeScale),
                    /* timeComplete		*/    Tweener._currentTimeFrame + ((rDelay + rTime) / Tweener._timeScale),
                    /* useFrames		*/    true,
                    /* transition		*/    rTransition,
                    p_obj.transitionParams
                );
            } else {
                nTween = new TweenListObj(
                    /* scope			*/    rScopes[i],
                    /* timeStart		*/    Tweener._currentTime + ((rDelay * 1000) / Tweener._timeScale),
                    /* timeComplete		*/    Tweener._currentTime + (((rDelay * 1000) + (rTime * 1000)) / Tweener._timeScale),
                    /* useFrames		*/    false,
                    /* transition		*/    rTransition,
                    p_obj.transitionParams
                );
            }

            nTween.properties = null;
            nTween.onStart = p_obj.onStart;
            nTween.onUpdate = p_obj.onUpdate;
            nTween.onComplete = p_obj.onComplete;
            nTween.onOverwrite = p_obj.onOverwrite;
            nTween.onStartParams = p_obj.onStartParams;
            nTween.onUpdateParams = p_obj.onUpdateParams;
            nTween.onCompleteParams = p_obj.onCompleteParams;
            nTween.onOverwriteParams = p_obj.onOverwriteParams;
            nTween.onStartScope = p_obj.onStartScope;
            nTween.onUpdateScope = p_obj.onUpdateScope;
            nTween.onCompleteScope = p_obj.onCompleteScope;
            nTween.onOverwriteScope = p_obj.onOverwriteScope;
            nTween.onErrorScope = p_obj.onErrorScope;
            nTween.isCaller = true;
            nTween.count = p_obj.count;
            nTween.waitFrames = p_obj.waitFrames;

            // And finally adds it to the list
            Tweener._tweenList.push(nTween);

            // Immediate update and removal if it's an immediate tween -- if not deleted, it executes at the end of this frame execution
            if (rTime == 0 && rDelay == 0) {
                myT = Tweener._tweenList.length - 1;
                Tweener.updateTweenByIndex(myT);
                Tweener.removeTweenByIndex(myT);
            }
        }

        return true;
    }

    /**
     * Remove an specified tweening of a specified object the tweening list, if it conflicts with the given time.
     *
     * @param        p_scope                Object                        List of objects affected
     * @param        p_properties        Object                        List of properties affected (PropertyInfoObj instances)
     * @param        p_timeStart            number                        Time when the new tween starts
     * @param        p_timeComplete        number                        Time when the new tween ends
     * @return                            boolean                        Whether or not it actually deleted something
     */
    public static removeTweensByTime(p_scope: Object, p_properties: any, p_timeStart: number, p_timeComplete: number): boolean {
        let removed: boolean = false;
        let removedLocally: boolean;

        let i: number;
        const tl: number = Tweener._tweenList.length;
        let pName: string;

        for (i = 0; i < tl; i++) {
            if (Tweener._tweenList[i] && p_scope == Tweener._tweenList[i].scope) {
                // Same object...
                if (p_timeComplete > Tweener._tweenList[i].timeStart && p_timeStart < Tweener._tweenList[i].timeComplete) {
                    // New time should override the old one...
                    removedLocally = false;
                    for (pName in Tweener._tweenList[i].properties) {
                        if (p_properties[pName]) {
                            // Same object, same property
                            // Finally, remove this old tweening and use the new one
                            if (Tweener._tweenList[i].onOverwrite) {
                                const eventScope: Object = Tweener._tweenList[i].onOverwriteScope ? Tweener._tweenList[i].onOverwriteScope : Tweener._tweenList[i].scope;
                                try {
                                    Tweener._tweenList[i].onOverwrite.apply(eventScope, Tweener._tweenList[i].onOverwriteParams);
                                } catch (e) {
                                    Tweener.handleError(Tweener._tweenList[i], e, 'onOverwrite');
                                }
                            }
                            Tweener._tweenList[i].properties[pName] = undefined;
                            delete Tweener._tweenList[i].properties[pName];
                            removedLocally = true;
                            removed = true;
                        }
                    }
                    if (removedLocally) {
                        // Verify if this can be deleted
                        if (AuxFunctions.getObjectLength(Tweener._tweenList[i].properties) == 0) Tweener.removeTweenByIndex(i);
                    }
                }
            }
        }

        return removed;
    }

    /*
    public static removeTweens (p_scope:Object, ...args):boolean {
        // Create the property list
        let properties:Array<any> = new Array();
        let i:number;
        for (i = 0; i < args.length; i++) {
            if (typeof(args[i]) == "string" && properties.indexOf(args[i]) == -1) properties.push(args[i]);
        }
        // Call the affect function on the specified properties
        return Tweener.affectTweens(Tweener.removeTweenByIndex, p_scope, properties);
    }
    */

    /**
     * Remove tweenings from a given object from the tweening list.
     *
     * @param p_scope Object that must have its tweens removed
     * @param args Property(ies) that must be removed
     * @return Whether or not it successfully removed this tweening
     */
    public static removeTweens(p_scope: Object, ...args: Array<any>): boolean {
        // Create the property list
        const properties: Array<any> = [];
        let i: number;
        for (i = 0; i < args.length; i++) {
            if (typeof (args[i]) == 'string' && properties.indexOf(args[i]) == -1) {
                if (Tweener._specialPropertySplitterList[args[i]]) {
                    //special property, get splitter array first
                    const sps: SpecialPropertySplitter = Tweener._specialPropertySplitterList[args[i]];
                    const specialProps: Array<any> = sps.splitValues(p_scope, null);
                    for (let j: number = 0; j < specialProps.length; j++) {
                        //trace(specialProps[j].name);
                        properties.push(specialProps[j].name);
                    }
                } else {
                    properties.push(args[i]);
                }
            }
        }

        // Call the affect function on the specified properties
        return Tweener.affectTweens(Tweener.removeTweenByIndex, p_scope, properties);
    }

    /**
     * Remove all tweenings from the engine.
     *
     * @return <code>true</code> if it successfully removed any tweening, <code>false</code> if otherwise.
     */
    public static removeAllTweens(): boolean {
        if (!(Tweener._tweenList)) return false;
        let removed: boolean = false;
        let i: number;
        for (i = 0; i < Tweener._tweenList.length; i++) {
            Tweener.removeTweenByIndex(i);
            removed = true;
        }
        return removed;
    }

    /**
     * Pause tweenings for a given object.
     *
     * @param p_scope Object that must have its tweens paused
     * @param args Property(ies) that must be paused
     *
     * @return <code>true</code> if it successfully paused any tweening, <code>false</code> if otherwise.
     */
    public static pauseTweens(p_scope: Object, ...args: Array<any>): boolean {
        // Create the property list
        const properties: Array<any> = [];
        let i: number;
        for (i = 0; i < args.length; i++) {
            if (typeof (args[i]) == 'string' && properties.indexOf(args[i]) == -1) properties.push(args[i]);
        }
        // Call the affect function on the specified properties
        return Tweener.affectTweens(Tweener.pauseTweenByIndex, p_scope, properties);
    }

    /**
     * Pause all tweenings on the engine.
     *
     * @return <code>true</code> if it successfully paused any tweening, <code>false</code> if otherwise.
     *
     * @see resumeAllTweens()
     */
    public static pauseAllTweens(): boolean {
        if (!(Tweener._tweenList)) return false;
        let paused: boolean = false;
        let i: number;
        for (i = 0; i < Tweener._tweenList.length; i++) {
            Tweener.pauseTweenByIndex(i);
            paused = true;
        }
        return paused;
    }

    /**
     * Resume tweenings from a given object.
     *
     * @param p_scope Object that must have its tweens resumed
     * @param args Property(ies) that must be resumed
     *
     * @return Whether or not it successfully resumed something
     */
    public static resumeTweens(p_scope: Object, ...args: Array<any>): boolean {
        // Create the property list
        const properties: Array<any> = [];
        let i: number;
        for (i = 0; i < args.length; i++) {
            if (typeof (args[i]) == 'string' && properties.indexOf(args[i]) == -1) properties.push(args[i]);
        }
        // Call the affect function on the specified properties
        return Tweener.affectTweens(Tweener.resumeTweenByIndex, p_scope, properties);
    }

    /**
     * Resume all tweenings on the engine.
     *
     * @return <code>true</code> if it successfully resumed any tweening, <code>false</code> if otherwise.
     *
     * @see pauseAllTweens()
     */
    public static resumeAllTweens(): boolean {
        if (!(Tweener._tweenList)) return false;
        let resumed: boolean = false;
        let i: number;
        for (i = 0; i < Tweener._tweenList.length; i++) {
            Tweener.resumeTweenByIndex(i);
            resumed = true;
        }
        return resumed;
    }

    /**
     * Do some generic action on specific tweenings (pause, resume, remove, more?)
     *
     * @param p_affectFunction Function to run on the tweenings that match
     * @param p_scope Object that must have its tweens affected by the function
     * @param p_properties Array of strings that must be affected
     *
     * @return Whether or not it successfully affected something
     */
    private static affectTweens(p_affectFunction: Function, p_scope: Object, p_properties: Array<any>): boolean {
        let affected: boolean = false;
        let i: number;

        if (!(Tweener._tweenList)) return false;

        for (i = 0; i < Tweener._tweenList.length; i++) {
            if (Tweener._tweenList[i] && Tweener._tweenList[i].scope == p_scope) {
                if (p_properties.length == 0) {
                    // Can affect everything
                    p_affectFunction(i);
                    affected = true;
                } else {
                    // Must check whether this tween must have specific properties affected
                    const affectedProperties: Array<any> = [];
                    let j: number;
                    for (j = 0; j < p_properties.length; j++) {
                        if (Tweener._tweenList[i].properties[p_properties[j]]) {
                            affectedProperties.push(p_properties[j]);
                        }
                    }
                    if (affectedProperties.length > 0) {
                        // This tween has some properties that need to be affected
                        const objectProperties: number = AuxFunctions.getObjectLength(Tweener._tweenList[i].properties);
                        if (objectProperties == affectedProperties.length) {
                            // The list of properties is the same as all properties, so affect it all
                            p_affectFunction(i);
                            affected = true;
                        } else {
                            // The properties are mixed, so split the tween and affect only certain specific properties
                            const slicedTweenIndex: number = Tweener.splitTweens(i, affectedProperties);
                            p_affectFunction(slicedTweenIndex);
                            affected = true;
                        }
                    }
                }
            }
        }
        return affected;
    }

    /**
     * Splits a tweening in two
     *
     * @param p_tween Object that must have its tweens split
     * @param p_properties Array of strings containing the list of properties that must be separated
     *
     * @return The index number of the new tween
     */
    public static splitTweens(p_tween: number, p_properties: Array<any>): number {
        // First, duplicates
        const originalTween: TweenListObj = Tweener._tweenList[p_tween];
        const newTween: TweenListObj = originalTween.clone(false);

        // Now, removes tweenings where needed
        let i: number;
        let pName: string;

        // Removes the specified properties from the old one
        for (i = 0; i < p_properties.length; i++) {
            pName = p_properties[i];
            if (originalTween.properties[pName]) {
                originalTween.properties[pName] = undefined;
                delete originalTween.properties[pName];
            }
        }

        // Removes the unspecified properties from the new one
        let found: boolean;
        for (pName in newTween.properties) {
            found = false;
            for (i = 0; i < p_properties.length; i++) {
                if (p_properties[i] == pName) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                newTween.properties[pName] = undefined;
                delete newTween.properties[pName];
            }
        }

        // If there are empty property lists, a cleanup is done on the next updateTweens() cycle
        Tweener._tweenList.push(newTween);
        return (Tweener._tweenList.length - 1);

    }

    // ==================================================================================================================================
    // ENGINE functions -----------------------------------------------------------------------------------------------------------------

    /**
     * Updates all existing tweenings.
     *
     * @return <code>false</code> if no update was made because there's no tweening (even delayed ones)
     */
    private static updateTweens(): boolean {
        if (Tweener._tweenList.length == 0) return false;
        let i: number;
        for (i = 0; i < Tweener._tweenList.length; i++) {
            // Looping throught each Tweening and updating the values accordingly
            if (Tweener._tweenList[i] == undefined || !Tweener._tweenList[i].isPaused) {
                if (!Tweener.updateTweenByIndex(i)) Tweener.removeTweenByIndex(i);
                if (Tweener._tweenList[i] == null) {
                    Tweener.removeTweenByIndex(i, true);
                    i--;
                }
            }
        }

        return true;
    }

    /**
     * Remove a specific tweening from the tweening list.
     *
     * @param p_tween Index of the tween to be removed on the tweenings list
     * @param p_finalRemoval
     *
     * @return Whether or not it successfully removed this tweening
     */
    public static removeTweenByIndex(p_tween: number, p_finalRemoval: boolean = false): boolean {
        Tweener._tweenList[p_tween] = null;
        if (p_finalRemoval) Tweener._tweenList.splice(p_tween, 1);
        return true;
    }

    /**
     * Pauses a specific tween.
     *
     * @param p_tween Index of the tween to be paused
     *
     * @return Whether or not it successfully paused this tweening
     */
    public static pauseTweenByIndex(p_tween: number): boolean {
        const tTweening: TweenListObj = Tweener._tweenList[p_tween];	// Shortcut to this tweening
        if (tTweening == null || tTweening.isPaused) return false;
        tTweening.timePaused = Tweener.getCurrentTweeningTime(tTweening);
        tTweening.isPaused = true;

        return true;
    }

    /**
     * Resumes a specific tween.
     *
     * @param p_tween Index of the tween to be resumed
     *
     * @return Whether or not it successfully resumed this tweening
     */
    public static resumeTweenByIndex(p_tween: number): boolean {
        const tTweening: TweenListObj = Tweener._tweenList[p_tween];	// Shortcut to this tweening
        if (tTweening == null || !tTweening.isPaused) return false;
        const cTime: number = Tweener.getCurrentTweeningTime(tTweening);
        tTweening.timeStart += cTime - tTweening.timePaused;
        tTweening.timeComplete += cTime - tTweening.timePaused;
        tTweening.timePaused = undefined;
        tTweening.isPaused = false;

        return true;
    }

    /**
     * Updates a specific tween.
     *
     * @param i Index (from the tween list) of the tween that should be updated
     *
     * @return <code>false</code> if it's already finished and should be deleted, <code>true</code> if otherwise
     */
    private static updateTweenByIndex(i: number): boolean {

        const tTweening: TweenListObj = Tweener._tweenList[i];	// Shortcut to this tweening

        if (tTweening == null || !(tTweening.scope)) return false;

        let isOver: boolean = false;		// Whether or not it's over the update time
        let mustUpdate: boolean;			// Whether or not it should be updated (skipped if false)

        let nv: number;					// New value for each property

        let t: number;					// current time (frames, seconds)
        let b: number;					// beginning value
        let c: number;					// change in value
        let d: number; 					// duration (frames, seconds)

        let pName: string;				// Property name, used in loops
        let eventScope: Object;			// Event scope, used to call functions

        // Shortcut stuff for speed
        let tScope: any;				// Current scope
        const cTime: number = Tweener.getCurrentTweeningTime(tTweening);
        let tProperty: any;			    // Property being checked

        if (cTime >= tTweening.timeStart) {
            // Can already start

            tScope = tTweening.scope;

            if (tTweening.isCaller) {
                // It's a 'caller' tween
                do {
                    t = ((tTweening.timeComplete - tTweening.timeStart) / tTweening.count) * (tTweening.timesCalled + 1);
                    b = tTweening.timeStart;
                    c = tTweening.timeComplete - tTweening.timeStart;
                    d = tTweening.timeComplete - tTweening.timeStart;
                    nv = tTweening.transition(t, b, c, d);

                    if (cTime >= nv) {
                        if (tTweening.onUpdate) {
                            eventScope = tTweening.onUpdateScope ? tTweening.onUpdateScope : tScope;
                            try {
                                tTweening.onUpdate.apply(eventScope, tTweening.onUpdateParams);
                            } catch (e1) {
                                Tweener.handleError(tTweening, e1, 'onUpdate');
                            }
                        }

                        tTweening.timesCalled++;
                        if (tTweening.timesCalled >= tTweening.count) {
                            isOver = true;
                            break;
                        }
                        if (tTweening.waitFrames) break;
                    }

                } while (cTime >= nv);
            } else {
                // It's a normal transition tween

                mustUpdate = tTweening.skipUpdates < 1 || !tTweening.skipUpdates || tTweening.updatesSkipped >= tTweening.skipUpdates;

                if (cTime >= tTweening.timeComplete) {
                    isOver = true;
                    mustUpdate = true;
                }

                if (!tTweening.hasStarted) {
                    // First update, read all default values (for proper filter tweening)
                    if (tTweening.onStart) {
                        eventScope = tTweening.onStartScope ? tTweening.onStartScope : tScope;
                        try {
                            tTweening.onStart.apply(eventScope, tTweening.onStartParams);
                        } catch (e2) {
                            Tweener.handleError(tTweening, e2, 'onStart');
                        }
                    }
                    let pv: number;
                    for (pName in tTweening.properties) {
                        if (tTweening.properties[pName].isSpecialProperty) {
                            // It's a special property, tunnel via the special property function
                            if (Tweener._specialPropertyList[pName].preProcess) {
                                tTweening.properties[pName].valueComplete = Tweener._specialPropertyList[pName].preProcess(tScope, Tweener._specialPropertyList[pName].parameters, tTweening.properties[pName].originalValueComplete, tTweening.properties[pName].extra);
                            }
                            pv = Tweener._specialPropertyList[pName].getValue(tScope, Tweener._specialPropertyList[pName].parameters, tTweening.properties[pName].extra);
                        } else {
                            // Directly read property
                            pv = tScope[pName];
                        }
                        tTweening.properties[pName].valueStart = isNaN(pv) ? tTweening.properties[pName].valueComplete : pv;
                    }
                    mustUpdate = true;
                    tTweening.hasStarted = true;
                }

                if (mustUpdate) {
                    for (pName in tTweening.properties) {
                        tProperty = tTweening.properties[pName];

                        if (isOver) {
                            // Tweening time has finished, just set it to the final value
                            nv = tProperty.valueComplete;
                        } else {
                            if (tProperty.hasModifier) {
                                // Modified
                                t = cTime - tTweening.timeStart;
                                d = tTweening.timeComplete - tTweening.timeStart;
                                nv = tTweening.transition(t, 0, 1, d, tTweening.transitionParams);
                                nv = tProperty.modifierFunction(tProperty.valueStart, tProperty.valueComplete, nv, tProperty.modifierParameters);
                            } else {
                                // Normal update
                                t = cTime - tTweening.timeStart;
                                b = tProperty.valueStart;
                                c = tProperty.valueComplete - tProperty.valueStart;
                                d = tTweening.timeComplete - tTweening.timeStart;
                                nv = tTweening.transition(t, b, c, d, tTweening.transitionParams);
                            }
                        }

                        if (tTweening.rounded) nv = Math.round(nv);
                        if (tProperty.isSpecialProperty) {
                            // It's a special property, tunnel via the special property method
                            Tweener._specialPropertyList[pName].setValue(tScope, nv, Tweener._specialPropertyList[pName].parameters, tTweening.properties[pName].extra);
                        } else {
                            // Directly set property
                            tScope[pName] = nv;
                        }
                    }

                    tTweening.updatesSkipped = 0;

                    if (tTweening.onUpdate) {
                        eventScope = tTweening.onUpdateScope ? tTweening.onUpdateScope : tScope;
                        try {
                            tTweening.onUpdate.apply(eventScope, tTweening.onUpdateParams);
                        } catch (e3) {
                            Tweener.handleError(tTweening, e3, 'onUpdate');
                        }
                    }
                } else {
                    tTweening.updatesSkipped++;
                }
            }

            if (isOver && tTweening.onComplete) {
                eventScope = tTweening.onCompleteScope ? tTweening.onCompleteScope : tScope;
                try {
                    tTweening.onComplete.apply(eventScope, tTweening.onCompleteParams);
                } catch (e4) {
                    Tweener.handleError(tTweening, e4, 'onComplete');
                }
            }

            return (!isOver);
        }

        // On delay, hasn't started, so returns true
        return true;
    }

    /**
     * Initiates the Tweener - should only run once.
     */
    public static init(): void {
        Tweener._inited = true;

        // Registers all default equations
        Tweener._transitionList = new Map<Transition, TransitionFunction>();
        Equations.init();

        // Registers all default special properties
        Tweener._specialPropertyList = {};
        Tweener._specialPropertyModifierList = {};
        Tweener._specialPropertySplitterList = {};
    }

    /**
     * Adds a new function to the available transition list "shortcuts".
     *
     * @param p_name Shorthand transition name
     * @param p_function The proper equation function
     */
    public static registerTransition(p_name: Transition, p_function: TransitionFunction): void {
        if (!Tweener._inited) Tweener.init();
        if (typeof p_name === 'string') {
            p_name = p_name.toLowerCase();
        }
        Tweener._transitionList.set(p_name, p_function);
    }

    /**
     * Adds a new special property to the available special property list.
     *
     * @param {string} p_name Name of the "special" property.
     * @param {Function} p_getFunction Function that gets the value.
     * @param {Function} p_setFunction Function that sets the value.
     * @param {Array<any>} p_parameters
     * @param {Function} p_preProcessFunction
     */
    public static registerSpecialProperty(p_name: string, p_getFunction: Function, p_setFunction: Function, p_parameters: Array<any> = null, p_preProcessFunction: Function = null): void {
        if (!Tweener._inited) Tweener.init();
        const sp: SpecialProperty = new SpecialProperty(p_getFunction, p_setFunction, p_parameters, p_preProcessFunction);
        Tweener._specialPropertyList[p_name] = sp;
    }

    /**
     * Adds a new special property modifier to the available modifier list.
     *
     * @param {string} p_name Name of the "special" property modifier.
     * @param {Function} p_modifyFunction Function that modifies the value.
     * @param {Function} p_getFunction Function that gets the value.
     */
    public static registerSpecialPropertyModifier(p_name: string, p_modifyFunction: Function, p_getFunction: Function): void {
        if (!Tweener._inited) Tweener.init();
        const spm: SpecialPropertyModifier = new SpecialPropertyModifier(p_modifyFunction, p_getFunction);
        Tweener._specialPropertyModifierList[p_name] = spm;
    }

    /**
     * Adds a new special property splitter to the available splitter list.
     *
     * @param {string} p_name Name of the "special" property splitter.
     * @param {Function} p_splitFunction Function that splits the value.
     * @param {Array<any>} p_parameters
     */
    public static registerSpecialPropertySplitter(p_name: string, p_splitFunction: Function, p_parameters: Array<any> = null): void {
        if (!Tweener._inited) Tweener.init();
        const sps: SpecialPropertySplitter = new SpecialPropertySplitter(p_splitFunction, p_parameters);
        Tweener._specialPropertySplitterList[p_name] = sps;
    }

    /**
     * Starts the Tweener class engine. It is supposed to be running every time a tween exists.
     */
    private static startEngine(): void {
        Tweener._engineExists = true;
        Tweener._tweenList = [];

        // window requestAnimationFrame polyfill
        if (window.requestAnimationFrame == null) {
            window.requestAnimationFrame = (function () {
                return window.requestAnimationFrame ||
                    window['webkitRequestAnimationFrame'] ||
                    window['mozRequestAnimationFrame'] as any ||
                    window['oRequestAnimationFrame'] ||
                    window['msRequestAnimationFrame'] ||
                    function (callback: any, element: any) {
                        window.setTimeout(callback, 1000 / 60);
                    };
            })();
        }

        window.requestAnimationFrame(Tweener.onEnterFrame);

        Tweener._currentTimeFrame = 0;
        Tweener.updateTime();
    }

    /**
     * Stops the Tweener class engine.
     */
    private static stopEngine(): void {
        Tweener._engineExists = false;
        Tweener._tweenList = null;
        Tweener._currentTime = 0;
        Tweener._currentTimeFrame = 0;
    }

    /**
     * Updates the time to enforce time grid-based updates.
     */
    public static updateTime(): void {
        Tweener._currentTime = new Date().getTime();
    }

    /**
     * Updates the current frame count
     */
    public static updateFrame(): void {
        Tweener._currentTimeFrame++;
    }

    /**
     * Ran once every frame. It's the main engine; updates all existing tweenings.
     */
    public static onEnterFrame(): void {
        Tweener.updateTime();
        Tweener.updateFrame();
        let hasUpdated: boolean = false;
        hasUpdated = Tweener.updateTweens();
        if (!hasUpdated) { // There's no tweening to update or wait, so it's better to stop the engine
            Tweener.stopEngine();
        } else {
            window.requestAnimationFrame(Tweener.onEnterFrame);
        }
    }

    /**
     * Sets the new timescale.
     *
     * @param p_time New time scale (0.5 = slow, 1 = normal, 2 = 2x fast forward, etc)
     */
    public static setTimeScale(p_time: number): void {
        let i: number;
        let cTime: number;

        if (isNaN(p_time)) p_time = 1;
        if (p_time < 0.00001) p_time = 0.00001;
        if (p_time != Tweener._timeScale) {
            if (Tweener._tweenList != null) {
                // Multiplies all existing tween times accordingly
                for (i = 0; i < Tweener._tweenList.length; i++) {
                    cTime = Tweener.getCurrentTweeningTime(Tweener._tweenList[i]);
                    Tweener._tweenList[i].timeStart = cTime - ((cTime - Tweener._tweenList[i].timeStart) * Tweener._timeScale / p_time);
                    Tweener._tweenList[i].timeComplete = cTime - ((cTime - Tweener._tweenList[i].timeComplete) * Tweener._timeScale / p_time);
                    if (Tweener._tweenList[i].timePaused != undefined) Tweener._tweenList[i].timePaused = cTime - ((cTime - Tweener._tweenList[i].timePaused) * Tweener._timeScale / p_time);
                }
            }
            // Sets the new timescale value (for new tweenings)
            Tweener._timeScale = p_time;
        }
    }


    // ==================================================================================================================================
    // AUXILIARY functions --------------------------------------------------------------------------------------------------------------

    /**
     * Finds whether or not an object has any tweening.
     *
     * @param p_scope Target object.
     *
     * @return <code>true</code> if there's a tweening occuring on this object (paused, delayed, or active), <code>false</code> if otherwise.
     */
    public static isTweening(p_scope: unknown): boolean {
        if (!(Tweener._tweenList)) return false;
        let i: number;

        for (i = 0; i < Tweener._tweenList.length; i++) {
            if (Tweener._tweenList[i] && Tweener._tweenList[i].scope === p_scope) {
                return true;
            }
        }
        return false;
    }

    /**
     * Returns an array containing a list of the properties being tweened for this object.
     *
     * @param p_scope Target object.
     *
     * @return Array of all properties being tweened (including delayed or paused tweens).
     */
    public static getTweens(p_scope: unknown): any[] {
        if (!(Tweener._tweenList)) return [];
        let i: number;
        let pName: string;
        const tList: any[] = [];

        for (i = 0; i < Tweener._tweenList.length; i++) {
            if (Tweener._tweenList[i] && Tweener._tweenList[i].scope === p_scope) {
                for (pName in Tweener._tweenList[i].properties) tList.push(pName);
            }
        }
        return tList;
    }

    /**
     * Returns the number of properties being tweened for a given object.
     *
     * @param p_scope Target object.
     *
     * @return Total number of properties being tweened (including delayed or paused tweens).
     */
    public static getTweenCount(p_scope: unknown): number {
        if (!(Tweener._tweenList)) return 0;
        let i: number;
        let c: number = 0;

        for (i = 0; i < Tweener._tweenList.length; i++) {
            if (Tweener._tweenList[i] && Tweener._tweenList[i].scope == p_scope) {
                c += AuxFunctions.getObjectLength(Tweener._tweenList[i].properties);
            }
        }
        return c;
    }


    /**
     * Handles errors when Tweener executes any callbacks (onStart, onUpdate, etc)
     * If the TweenListObj specifies an <code>onError</code> callback it will get called, passing the <code>Error</code> object and the current scope as parameters. If no <code>onError</code> callback is specified, it will trace a stackTrace.
     */
    private static handleError(pTweening: TweenListObj, pError: Error, pCallBackName: string): void {
        // do we have an error handler?
        if (pTweening.onError && (pTweening.onError instanceof Function)) {
            // yup, there's a handler. Wrap this in a try catch in case the onError throws an error itself.
            const eventScope: Object = pTweening.onErrorScope ? pTweening.onErrorScope : pTweening.scope;
            try {
                pTweening.onError.apply(eventScope, [pTweening.scope, pError]);
            } catch (metaError) {
                Tweener.printError(pTweening.scope + ' raised an error while executing the \'onError\' handler. Original error:\n ' + pError.stack + '\nonError error: ' + metaError.stack);
            }
        } else {
            // no handler, simply trace the stack trace:
            if (!(pTweening.onError)) {
                Tweener.printError(pTweening.scope + ' raised an error while executing the \'' + pCallBackName + '\'handler. \n' + pError.stack);
            }
        }
    }

    /**
     * Get the current tweening time (no matter if it uses frames or time as basis), given a specific tweening
     *
     * @param p_tweening Tween information
     */
    public static getCurrentTweeningTime(p_tweening: TweenListObj): number {
        return p_tweening.useFrames ? Tweener._currentTimeFrame : Tweener._currentTime;
    }

    /**
     * Return the current Tweener version
     *
     * @return The number of the current Tweener version
     */
    public static getVersion(): string {
        return '1.0.2';
    }


    // ==================================================================================================================================
    // DEBUG functions ------------------------------------------------------------------------------------------------------------------

    /**
     * Output an error message
     *
     * @param p_message The error message to output
     */
    public static printError(p_message: string): void {
        console.log('## [Tweener] Error: ' + p_message);
    }
}
